import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useProcessDocument from "@fartherfinance/frontend/api/Document/hooks/useProcessDocument";
import { DocumentUrl } from "@fartherfinance/frontend/api/Document/types";
import useGetClient from "@fartherfinance/frontend/api/Entity/hooks/useGetClient";
import { PersonId } from "@fartherfinance/frontend/api/Investment/types";
import useGetTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposal";
import usePostAssociateTransitionProposalOcr from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostAssociateTransitionProposalOcr";
import { usePostShareTransitionProposal } from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostShareTransitionProposal";
import usePostTransitionProposalExistingHoldings from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostTransitionProposalExistingHoldings";
import { TransitionProposalId } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId, VirtualAccountId } from "@fartherfinance/frontend/api/Types";
import { TransitionProposalStepName } from "@fartherfinance/frontend/eventTracking/transitionProposalEvents";

import { importTypes } from "../constants";
import { strings } from "../strings";
import ConfirmShareProposalModal from "../TransitionProposalFlowSteps/Share/ConfirmShareProposalModal/ConfirmShareProposalModal";
import CreateCustomModelModal from "../TransitionProposalFlowSteps/Share/CreateCustomModelModal";
import TransitionProposalFlowSteps from "../TransitionProposalFlowSteps/TransitionProposalFlowSteps";
import {
  TransitionProposalFlowPageIndex,
  TransitionProposalFlowStateData,
} from "../types";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { useUploadFiles } from "@src/multiCustodian/pages/Client/:clientId/DocumentsCenter/UploadFilesProvider/UploadFilesProvider";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import useBookmark from "@src/sharedComponents/BookmarkButton/useBookmark";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import FlowContainer from "@src/sharedComponents/FlowContainer/FlowContainer";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import { State } from "@src/store";
import { handleApiError } from "@src/utils/handleApiError";

import styles from "./TransitionProposalFlow.module.css";

const TransitionProposalFlow: React.FC = () => {
  const { clientId, transitionProposalId } = useParams<{
    clientId: ClientId;
    transitionProposalId: TransitionProposalId;
  }>();
  const navigate = useNavigate();
  const auth = useRequestAuth();
  const notify = useStatusNotification();
  const bookmark = useBookmark();
  const dispatchMain = useDispatch();

  const {
    data: transitionProposalResponseData,
    isLoading: isLoadingTransitionProposal,
  } = useGetTransitionProposal(transitionProposalId, auth);

  const transitionProposal = useMemo(() => {
    return transitionProposalResponseData?.data;
  }, [transitionProposalResponseData]);

  const clientProfile = useGetClient(clientId, auth);

  const clientFullName = useMemo(() => {
    if (clientProfile.data === undefined) {
      return "";
    }

    const { first: firstName = "", last: lastName = "" } =
      clientProfile.data.name ?? {};

    return lastName ? `${firstName} ${lastName}` : firstName;
  }, [clientProfile.data]);

  const globalAlert = useSelector(
    (state: State) => state.main_Reducer.global_alert
  );

  const {
    mutateAsync: shareTransitionProposal,
    isLoading: isMutatingShareTransitionProposal,
  } = usePostShareTransitionProposal(auth);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleConfirmShareTransitionProposal = useCallback(async () => {
    if (transitionProposalId === undefined) {
      return;
    }

    try {
      await shareTransitionProposal({ body: {}, transitionProposalId });

      // Track sharing event
      trackEvent({
        name: "Advisor Share Transition-Proposal",
        properties: {
          proposalId: transitionProposalId,
          clientId,
        },
      });

      notify(strings.PROPOSAL_SHARED, "Success");
      setIsShareModalOpen(false);
      navigate({
        pathname: `/Client/${clientId}/Investments/TransitionProposals`,
      });
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_SHARING_PROPOSAL,
        genericErrorMessage: strings.ERROR_SHARING_PROPOSAL_GENERIC,
        notify: notify,
      });
    }
  }, [
    shareTransitionProposal,
    notify,
    setIsShareModalOpen,
    transitionProposalId,
    navigate,
    clientId,
  ]);

  const handleOnOpenShareModal = () => {
    setIsShareModalOpen(true);
  };

  const handleOnCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  useEffect(() => {
    if (globalAlert !== null) {
      dispatchMain({ type: "CLEAR_GLOBAL_ALERT_DATA" });
    }
  }, [dispatchMain, globalAlert]);

  const isFartherRecordsPath = (): boolean => {
    return decodeURIComponent(location.pathname).endsWith("/Statements");
  };

  const bucketName =
    process.env.WEBAPP_ENV === "UAT"
      ? "uat-docs.farther.com"
      : "docs.farther.com";

  const {
    state: { progressState, pageIndex, data },
    dispatch,
  } = useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const { checkedfiles } = useUploadFiles();

  const { mutateAsync: processDocument, isLoading: isProcessingDocument } =
    useProcessDocument(auth);

  const {
    mutateAsync: associateTransitionProposalOcr,
    isLoading: isMutatingAssociateProposal,
  } = usePostAssociateTransitionProposalOcr(auth);

  const getStepName = (index: number): TransitionProposalStepName => {
    switch (index) {
      case TransitionProposalFlowPageIndex.Start:
        return "details";
      case TransitionProposalFlowPageIndex.Add:
        return "upload";
      case TransitionProposalFlowPageIndex.Review:
        return "review";
      case TransitionProposalFlowPageIndex.Share:
        return "share";
      default:
        // This fallback is needed for type safety, but should not occur in normal flow
        return "details";
    }
  };

  const handleOnClickBack = () => {
    if (
      pageIndex === TransitionProposalFlowPageIndex.Add &&
      transitionProposal?.importType === importTypes.STATEMENT &&
      !isFartherRecordsPath()
    ) {
      navigate(-1);
    } else {
      const prevPageIndex = pageIndex - 1;

      // Track step navigation
      if (transitionProposalId && prevPageIndex >= 0) {
        trackEvent({
          name: "Advisor Navigate Transition-Proposal Step",
          properties: {
            fromStep: getStepName(pageIndex),
            toStep: getStepName(prevPageIndex),
            proposalId: transitionProposalId,
          },
        });
      }

      dispatch({ type: actions.DECREMENT_PAGE_INDEX });
    }
  };

  const processStatementImport = useCallback(async () => {
    try {
      const res = await processDocument({
        personId: PersonId.parse(clientId),
        documents: checkedfiles.map((file) => file.path) as DocumentUrl[],
        bucket: bucketName,
      });

      const associateBody = {
        transitionProposalId,
        ocrDocumentIds: res.processingOcrDocuments.map((doc) => doc.documentId),
      };

      await associateTransitionProposalOcr({
        body: associateBody,
        transitionProposalId,
      });

      navigate({
        pathname: `/Client/${clientId}/Investments/TransitionProposals`,
        search: new URLSearchParams({ processing: "true" }).toString(),
      });
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_PROCESSING_DOCUMENTS,
        genericErrorMessage: strings.ERROR_PROCESSING_DOCUMENTS_GENERIC,
        notify,
      });
    }
  }, [
    clientId,
    checkedfiles,
    notify,
    processDocument,
    navigate,
    bucketName,
    transitionProposalId,
    associateTransitionProposalOcr,
  ]);

  const {
    mutateAsync: addExistingTransitionProposalHoldings,
    isLoading: isMutatingExistingHoldings,
  } = usePostTransitionProposalExistingHoldings(auth, transitionProposalId);

  const handleOnAddExistingTransitionProposalHoldings = async () => {
    const virtualAccountIds = (data.resources || []).map(
      (resource) => resource.resourceId
    ) as VirtualAccountId[];
    try {
      await addExistingTransitionProposalHoldings({
        body: {
          virtualAccountIds,
        },
        transitionProposalId,
      });

      dispatch({
        type: actions.SET_PAGE_INDEX,
        payload: TransitionProposalFlowPageIndex.Review,
      });

      notify(strings.HOLDINGS_UPDATED, "Success");
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_UPDATING_HOLDINGS,
        genericErrorMessage: strings.ERROR_UPDATING_HOLDINGS_GENERIC,
        notify,
      });
    }
  };

  const handleOnClickNext = async () => {
    if (pageIndex === TransitionProposalFlowPageIndex.Share) {
      navigate(`/Client/${clientId}/Investments/TransitionProposals`);
      return;
    }

    if (pageIndex !== TransitionProposalFlowPageIndex.Add) {
      const nextPageIndex = pageIndex + 1;

      // Track step navigation
      if (transitionProposalId) {
        trackEvent({
          name: "Advisor Navigate Transition-Proposal Step",
          properties: {
            fromStep: getStepName(pageIndex),
            toStep: getStepName(nextPageIndex),
            proposalId: transitionProposalId,
          },
        });
      }

      dispatch({ type: actions.INCREMENT_PAGE_INDEX });
      return;
    }

    if (transitionProposal?.importType === undefined) {
      throw new Error("Import type is undefined");
    }

    switch (transitionProposal.importType) {
      case importTypes.STATEMENT:
        await processStatementImport();
        break;
      case importTypes.EXISTING:
        await handleOnAddExistingTransitionProposalHoldings();
        break;
      default:
        const nextPageIndex = pageIndex + 1;

        // Track step navigation
        if (transitionProposalId) {
          trackEvent({
            name: "Advisor Navigate Transition-Proposal Step",
            properties: {
              fromStep: getStepName(pageIndex),
              toStep: getStepName(nextPageIndex),
              proposalId: transitionProposalId,
            },
          });
        }

        dispatch({ type: actions.INCREMENT_PAGE_INDEX });
        break;
    }
  };

  const disableNext = useMemo(() => {
    if (
      isProcessingDocument ||
      isMutatingAssociateProposal ||
      transitionProposal === undefined
    ) {
      return true;
    }

    if (pageIndex === TransitionProposalFlowPageIndex.Add) {
      if (
        transitionProposal.importType === importTypes.STATEMENT &&
        checkedfiles.length === 0
      ) {
        return true;
      }

      if (
        transitionProposal.importType === importTypes.EXISTING &&
        (data.resources === undefined || data.resources.length === 0)
      ) {
        return true;
      }
    }

    return false;
  }, [
    isProcessingDocument,
    isMutatingAssociateProposal,
    transitionProposal,
    pageIndex,
    checkedfiles,
    data.resources,
  ]);

  const disablePrev = useMemo(() => {
    if (transitionProposal?.status === "shared") {
      return true;
    }

    if (
      isProcessingDocument ||
      isMutatingAssociateProposal ||
      transitionProposal === undefined
    ) {
      return true;
    }

    if (
      transitionProposal.status === "processed" &&
      pageIndex === TransitionProposalFlowPageIndex.Review
    ) {
      return true;
    }

    if (
      transitionProposal.importType === "manual" &&
      pageIndex <= TransitionProposalFlowPageIndex.Review
    ) {
      return true;
    }

    return false;
  }, [
    isProcessingDocument,
    isMutatingAssociateProposal,
    transitionProposal,
    pageIndex,
  ]);

  const generateTitleBlockText = () => {
    if (transitionProposal === undefined) {
      return { title: "", subTitle: "" };
    }

    const importType = transitionProposal.importType;

    if (pageIndex === TransitionProposalFlowPageIndex.Add) {
      return importType === importTypes.STATEMENT
        ? {
            title: strings.FLOW_STEP_ADD_STATEMENT_TITLE,
            subTitle: strings.FLOW_STEP_ADD_STATEMENT_SUBTITLE,
          }
        : {
            title: strings.FLOW_STEP_ADD_EXISTING_TITLE,
            subTitle: strings.FLOW_STEP_ADD_EXISTING_SUBTITLE,
          };
    }

    if (pageIndex === TransitionProposalFlowPageIndex.Review) {
      return undefined;
    }

    return {
      title: strings.FLOW_STEP_START_TITLE,
      subTitle: strings.FLOW_STEP_START_SUBTITLE,
    };
  };

  const showLoading =
    isProcessingDocument ||
    isMutatingAssociateProposal ||
    isMutatingExistingHoldings;

  const showCustomPortFolioModal = useMemo(() => {
    if (data?.showCustomPortfolioModal === undefined) {
      return false;
    }
    return data.showCustomPortfolioModal;
  }, [data]);

  const closeCustomPortfolioModal = useCallback(() => {
    dispatch({
      type: actions.SET_DATA,
      payload: { showCustomPortfolioModal: false },
    });
  }, [dispatch]);

  const handleCreateCustomModel = useCallback(() => {
    bookmark.saveLocationAndNavigate(
      {
        pathname: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2`,
        search: "?openModal=true",
      },
      "Proposal"
    );
    closeCustomPortfolioModal();
  }, [bookmark, closeCustomPortfolioModal]);

  const nextButtonText = useMemo(() => {
    if (pageIndex === TransitionProposalFlowPageIndex.Share) {
      return "Finish";
    }
    return "Continue";
  }, [pageIndex]);

  const shareButtonIsDisabled = useMemo(() => {
    if (isLoadingTransitionProposal) {
      return true;
    }
    if (transitionProposal?.proposedPortfolioPlanId === null) {
      return true;
    }
    if (transitionProposal?.status === "shared") {
      return true;
    }
    // Check if there are any errors in the TransitionProposalDetails components
    if (data?.hasTransitionProposalDetailsError) {
      return true;
    }
    return false;
  }, [
    transitionProposal,
    isLoadingTransitionProposal,
    data?.hasTransitionProposalDetailsError,
  ]);

  return (
    <>
      <CreateCustomModelModal
        isOpen={showCustomPortFolioModal}
        onClose={closeCustomPortfolioModal}
        onCreateCustomModel={handleCreateCustomModel}
      />

      {transitionProposal !== undefined && (
        <ConfirmShareProposalModal
          isOpen={isShareModalOpen}
          onClose={handleOnCloseShareModal}
          onConfirmShare={handleConfirmShareTransitionProposal}
          isLoading={isMutatingShareTransitionProposal}
          transitionProposal={transitionProposal}
          clientFullName={clientFullName}
        />
      )}

      <FlowContainer
        onClickBack={handleOnClickBack}
        onClickNext={handleOnClickNext}
        onClickExit={`/Client/${clientId}/Investments/TransitionProposals`}
        progressState={progressState}
        pageIndex={pageIndex}
        disableNext={disableNext || showLoading}
        disablePrev={disablePrev}
        nextButtonText={nextButtonText}
        fluid
        innerContentClassName={styles.innerContent}
        pageHeroEndComponent={
          <Conditional
            condition={
              pageIndex === TransitionProposalFlowPageIndex.Share &&
              transitionProposal?.status !== "shared"
            }
          >
            <Button
              text={strings.SHARE_PROPOSAL_BUTTON}
              variant="contained"
              buttonType="primary"
              disabled={shareButtonIsDisabled}
              onClick={handleOnOpenShareModal}
            />
          </Conditional>
        }
      >
        <TransitionProposalFlowSteps
          header={
            generateTitleBlockText() !== undefined && (
              <TitleBlock
                className={styles.bodyTitleBlock}
                titleVariant="title2"
                title={generateTitleBlockText()?.title || ""}
                subTitle={generateTitleBlockText()?.subTitle || ""}
              />
            )
          }
        />
        {showLoading && <LogoLoadingStill onTop />}
      </FlowContainer>
    </>
  );
};

export default TransitionProposalFlow;
